// iter160 — Mobil App-Tarzı Alt Dock (Bottom Navigation Bar)
// Sadece < md ekranlarda görünür. İçerik: [Menu · Mağazalar · Arama · Sepet · Profil]
// Menu → kategoriler bottom sheet · Mağazalar → mağaza sheet · Arama → tam ekran search sheet
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, MapPin, Search, ShoppingCart, User, X, Store as StoreIcon, Package, ChevronRight, Navigation } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { useCompany } from "../../context/CompanyContext";
import { api } from "../../lib/api";
import useShopTree from "../../hooks/useShopTree";
import { AdvancedSearch } from "./PublicHeader";

function BottomSheet({ open, onClose, title, children, height = "80vh" }) {
  useEffect(() => {
    if (open) { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] md:hidden" data-testid={`bottom-sheet-${title?.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}/>
      <div
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 flex flex-col"
        style={{ maxHeight: height }}
      >
        <div className="pt-2 pb-1 flex justify-center"><div className="w-10 h-1 rounded-full bg-zinc-200"/></div>
        <div className="px-5 pb-3 flex items-center justify-between border-b border-zinc-100">
          <div className="font-display font-semibold text-zinc-900 text-lg">{title}</div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-zinc-100" aria-label="Kapat">
            <X size={18}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>
  );
}

function CategoryTree({ onNavigate, brandPrimary }) {
  const { tree } = useShopTree();
  const cats = (tree.categories || []).filter(c => (c.product_count || 0) > 0 || (c.children || []).length > 0);
  const [openIds, setOpenIds] = useState(new Set());
  const toggle = (id) => setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (cats.length === 0) {
    return <div className="p-10 text-center text-sm text-zinc-400">Kategoriler yükleniyor…</div>;
  }
  return (
    <div className="p-3 space-y-1" data-testid="mobile-cat-tree">
      {cats.map(c => {
        const hasKids = (c.children || []).length > 0;
        const isOpen = openIds.has(c.id);
        return (
          <div key={c.id} data-testid={`mobile-cat-${c.slug || c.id}`}>
            <div className="flex items-center rounded-xl hover:bg-zinc-50">
              <Link to={`/kategori/${c.slug || c.id}`} onClick={onNavigate}
                    className="flex-1 px-4 py-3 text-[15px] font-medium text-zinc-900">
                {c.name}
              </Link>
              {hasKids && (
                <button onClick={() => toggle(c.id)} className="w-11 h-11 flex items-center justify-center text-zinc-400"
                        aria-label={isOpen ? "Kapat" : "Aç"}>
                  <ChevronRight size={16} className={`transition-transform ${isOpen ? "rotate-90" : ""}`}/>
                </button>
              )}
            </div>
            {hasKids && isOpen && (
              <div className="pl-4 pb-2 space-y-0.5">
                {c.children.map(sc => (
                  <Link key={sc.id} to={`/kategori/${sc.slug || sc.id}`} onClick={onNavigate}
                        className="block px-4 py-2.5 text-[13px] text-zinc-600 rounded-lg hover:bg-zinc-50">
                    {sc.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StoreList({ onNavigate, brandPrimary }) {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    api.get("/seo/stores").then(r => setStores(Array.isArray(r.data) ? r.data : (r.data?.stores || []))).catch(() => {});
  }, []);
  const dirs = (s) => s.maps?.google_directions
    || (s.lat && s.lng ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
                       : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((s.name || "") + " " + (s.address || ""))}`);
  if (stores.length === 0) return <div className="p-10 text-center text-sm text-zinc-400">Mağazalar yükleniyor…</div>;
  return (
    <div className="p-4 space-y-3" data-testid="mobile-store-list">
      {stores.map(s => (
        <div key={s.id} className="rounded-2xl border border-zinc-100 p-4" data-testid={`mobile-store-${s.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <StoreIcon size={16}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-zinc-900">{s.name || s.district || "Mağaza"}</div>
              <div className="text-[12px] text-zinc-600 leading-snug mt-0.5">{s.address}</div>
              {s.phone && <a href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`} className="text-[12px] text-primary font-semibold mt-1 inline-block">{s.phone}</a>}
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <a href={dirs(s)} target="_blank" rel="noreferrer"
               className="flex-1 text-center px-3 py-2 rounded-full text-white text-[12px] font-semibold"
               style={{ background: brandPrimary }}>
              <Navigation size={12} className="inline mr-1"/> Yol Tarifi
            </a>
            <Link to={`/magaza/${s.id}`} onClick={onNavigate}
                  className="flex-1 text-center px-3 py-2 rounded-full border border-zinc-200 text-[12px] font-semibold text-zinc-700">
              Detay
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MobileDock() {
  const nav = useNavigate();
  const { count } = useCart();
  const { customer } = useCustomerAuth();
  const { company } = useCompany();
  const brandPrimary = company?.brand_primary || "#8D1B3D";
  const [sheet, setSheet] = useState(null); // "cats" | "stores" | "search" | null
  const close = () => setSheet(null);

  return (
    <>
      {/* Alt dock — sadece mobil */}
      <nav
        className="md:hidden fixed left-0 right-0 bottom-0 z-40 bg-white/95 backdrop-blur-xl border-t border-zinc-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-dock"
      >
        <div className="grid grid-cols-5 h-16 items-center">
          <button onClick={() => setSheet("cats")} className="flex flex-col items-center gap-0.5 text-zinc-600 active:scale-95 transition-transform" data-testid="dock-menu">
            <Menu size={22}/><span className="text-[10px] font-medium">Menü</span>
          </button>
          <button onClick={() => setSheet("stores")} className="flex flex-col items-center gap-0.5 text-zinc-600 active:scale-95 transition-transform" data-testid="dock-stores">
            <MapPin size={22}/><span className="text-[10px] font-medium">Mağazalar</span>
          </button>
          {/* Merkez: Arama — bordo dolu, çıkıntılı FAB stili */}
          <button onClick={() => setSheet("search")} className="relative flex flex-col items-center active:scale-95 transition-transform" data-testid="dock-search">
            <span
              className="absolute -top-6 w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg ring-4 ring-white"
              style={{ background: brandPrimary }}
            >
              <Search size={22}/>
            </span>
            <span className="text-[10px] font-medium text-zinc-600 mt-9">Ara</span>
          </button>
          <Link to="/sepet" className="relative flex flex-col items-center gap-0.5 text-zinc-600 active:scale-95 transition-transform" data-testid="dock-cart">
            <div className="relative">
              <ShoppingCart size={22}/>
              {count > 0 && (
                <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                      style={{ background: brandPrimary }}>
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">Sepet</span>
          </Link>
          <Link to={customer ? "/hesap" : "/hesap/giris"} className="flex flex-col items-center gap-0.5 text-zinc-600 active:scale-95 transition-transform" data-testid="dock-profile">
            <User size={22}/><span className="text-[10px] font-medium">{customer ? "Hesap" : "Giriş"}</span>
          </Link>
        </div>
      </nav>

      <BottomSheet open={sheet === "cats"} onClose={close} title="Kategoriler" height="85vh">
        <CategoryTree onNavigate={close} brandPrimary={brandPrimary}/>
      </BottomSheet>

      <BottomSheet open={sheet === "stores"} onClose={close} title="Mağazalarımız" height="80vh">
        <StoreList onNavigate={close} brandPrimary={brandPrimary}/>
      </BottomSheet>

      <BottomSheet open={sheet === "search"} onClose={close} title="Arama" height="90vh">
        <div className="p-4">
          <AdvancedSearch brandPrimary={brandPrimary} inline autoFocus/>
          <div className="mt-6 text-center text-[11px] text-zinc-400">
            Ürün adı, marka, kod veya AI'ya sorabilirsiniz — Bordo AI size ürün önerir.
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
